import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiGet } from '../../../../../generic/Api_Functions'
import useMosInfo from '../../../common/provider/useMosInfo'
import { useSmartInterval } from '../../../../../generic/hooks/useSmartInterval'

const safeTime = (value) => {
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time
}

export const useIncidenceHistory = (options = {}) => {
  const { enabled = true, refreshMs = 15000, maxItems = 200 } = options
  const { device } = useMosInfo()

  const [incidences, setIncidences] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const isInFlightRef = useRef(false)
  const hasDataRef = useRef(false)

  const loadIncidences = useCallback(async (opts = {}) => {
    const { showLoading = false } = opts
    if (!enabled) return
    if (!device?.id) return
    if (isInFlightRef.current) return

    setError(null)
    if (showLoading) setLoading(true)
    isInFlightRef.current = true
    try {
      const res = await apiGet(`mos/incidences/${device.id}/${false}`)
      const next = Array.isArray(res) ? res : []
      hasDataRef.current = next.length > 0
      setIncidences(next)
    } catch (e) {
      console.error('Error loading incidence history:', e)
      setError(e)
    } finally {
      isInFlightRef.current = false
      if (showLoading) setLoading(false)
    }
  }, [device?.id, enabled])

  const pollIncidences = useCallback(async () => {
    await loadIncidences({ showLoading: false })
  }, [loadIncidences])

  const setIntervalEnabled = useSmartInterval(pollIncidences, refreshMs, () => {}, false)
  const setIntervalEnabledRef = useRef(null)

  useEffect(() => {
    setIntervalEnabledRef.current = setIntervalEnabled
  }, [setIntervalEnabled])

  useEffect(() => {
    const shouldPoll = Boolean(enabled && device?.id)
    if (typeof setIntervalEnabledRef.current !== 'function') return
    setIntervalEnabledRef.current(shouldPoll, false)
    if (shouldPoll) {
      loadIncidences({ showLoading: !hasDataRef.current })
    }
  }, [device?.id, enabled, loadIncidences])

  const sortedIncidences = useMemo(() => {
    return [...incidences].sort((a, b) => safeTime(b?.startDate) - safeTime(a?.startDate)).slice(0, maxItems)
  }, [incidences, maxItems])

  return { incidences: sortedIncidences, loading, error, reload: loadIncidences }
}
